import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosInstance, AxiosRequestConfig } from 'axios';

type RequestConfigType = ReadonlyArray<Pick<AxiosRequestConfig, 'url' | 'headers' | 'data' | 'params' | 'timeout' | 'method'>>;
type RequestConfigTypeCreator<T, M extends string, V> = Omit<RequestConfigTypeFilter<T, M, V>, M>;
type RequestConfigTypeFilter<T, K, V> = {
    [P in keyof T]: K extends keyof T ? (T[K] extends V ? T : never) : never;
}[keyof T];

// const ApiList = [
//     { url: '/api/ip', method: 'get' },
//     { url: '/api/ip', method: 'post' },
// ] as const;

// type b = (typeof ApiList)[number];
// type a = RequestConfigTypeFilter<b, 'method', 'get'>;
// const d: a = {
//     url: '/api/ip',
// };

export type AxiosParamsType = {
    baseURL?: string;
    instanceConfig?: Record<string, any>;
    interceptorsReq?: (config: InternalAxiosRequestConfig<any>) => void;
    interceptorsRes?: (response: AxiosResponse) => void;
    interceptorsReqError?: (error: AxiosError) => Promise<any>;
    interceptorsResError?: (error: AxiosError) => Promise<any>;
};

export class Request<T extends RequestConfigType, D = T[number]> {
    instance: AxiosInstance;
    constructor(params?: AxiosParamsType) {
        // 创建 Axios 实例
        const instance = axios.create({
            baseURL: params?.baseURL || '', // 设置基本的 API URL
            timeout: 10000, // 设置请求超时时间
            ...params?.instanceConfig,
        });

        // 请求拦截器
        instance.interceptors.request.use(
            (config: InternalAxiosRequestConfig<any>) => {
                // 在请求发送之前可以进行一些操作，比如添加请求头
                params?.interceptorsReq && params?.interceptorsReq(config);
                return config;
            },
            (error: AxiosError) => {
                return (params?.interceptorsReqError && params.interceptorsReqError(error)) || Promise.reject(error);
            },
        );

        // 响应拦截器
        instance.interceptors.response.use(
            (response: AxiosResponse) => {
                // 在响应返回之前可以进行一些操作，比如处理错误码等
                params?.interceptorsRes && params?.interceptorsRes(response);
                return response;
            },
            (error: AxiosError) => {
                return (params?.interceptorsResError && params.interceptorsResError(error)) || Promise.reject(error);
            },
        );

        this.instance = instance;
    }
    get = async (
        options: Omit<RequestConfigTypeCreator<D, 'method', 'get' | 'GET'>, 'params' | 'headers' | 'responseType'> & {
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
            responseType?: AxiosRequestConfig['responseType'];
            pathParams?: string | number;
        },
    ) => {
        if (options.pathParams) {
            options['url' as keyof typeof options] = `${options['url' as keyof typeof options]}/${options.pathParams}`;
            delete options.pathParams;
        }
        const response = await this.instance.request({
            ...options,
            method: 'GET',
        });
        return response;
    };
    post = async (
        options: Omit<RequestConfigTypeCreator<D, 'method', 'post' | 'POST'>, 'data' | 'params' | 'headers'> & {
            data?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
            responseType?: AxiosRequestConfig['responseType'];
            pathParams?: string | number;
        },
    ) => {
        if (options.pathParams) {
            options['url' as keyof typeof options] = `${options['url' as keyof typeof options]}/${options.pathParams}`;
            delete options.pathParams;
        }
        const response = await this.instance.request({
            ...options,
            method: 'POST',
        });
        return response;
    };
    put = async (
        options: Omit<RequestConfigTypeCreator<D, 'method', 'put' | 'PUT'>, 'data' | 'params' | 'headers'> & {
            data?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
        },
    ) => {
        const response = await this.instance.request({
            ...options,
            method: 'PUT',
        });
        return response;
    };
    delete = async (
        options: Omit<RequestConfigTypeCreator<D, 'method', 'delete' | 'DELETE'>, 'data' | 'params' | 'headers'> & {
            pathParams?: string | number;
            data?: AxiosRequestConfig['data'];
            params?: AxiosRequestConfig['params'];
            headers?: AxiosRequestConfig['headers'];
        },
    ) => {
        if (options.pathParams) {
            options['url' as keyof typeof options] = `${options['url' as keyof typeof options]}/${options.pathParams}`;
            delete options.pathParams;
        }
        const response = await this.instance.request({
            ...options,
            method: 'DELETE',
        });
        return response;
    };
}
